import React from 'react';
import { useSelector } from 'react-redux';
import { sortBy } from 'lodash';
import moment from 'moment';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { paymentSelectors } from '../../../../../app/resource/payment/payment.selectors';
import { paymentMethodSelectors } from '../../../../../app/resource/payment-method/payment-method.selectors';
import { IPayment } from '../../../../../app/resource/payment/payment.types';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { formatCentsAsCurrency } from '../../../../../utils/currency.utils';
import { formatUtcDateToUserTimeZone } from '../../../../../utils/date.utils';
import { useSelectWithParams } from '../../../../../utils/selector.utils';

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'succeeded':
      return { label: 'Processed', className: 'badge lightGreen-bg green' };
    case 'pending':
    case 'processing':
      return { label: status === 'pending' ? 'Pending' : 'Processing', className: 'badge lightYellow-bg yellow' };
    case 'failed':
      return { label: 'Failed', className: 'badge lightRed-bg red' };
    case 'cancelled':
      return { label: 'Cancelled', className: 'badge lightRed-bg red' };
    default:
      return { label: status, className: 'badge lightBlue-bg blue' };
  }
};

export const Payments = () => {
  const payments = useSelector(paymentSelectors.selectResourceList) as IPayment[];
  const paymentMethods = useSelector(paymentMethodSelectors.selectResourceList);
  const loggedUserId = useSelector(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const userTimezone = loggedUser?.timezone ?? null;

  const sortedPayments = sortBy(payments, (payment) =>
    payment.createdAt ? moment.utc(payment.createdAt).valueOf() : 0,
  ).reverse();

  const getPaymentMethodLabel = (payment: IPayment) => {
    const brand = (payment.paymentMethodBrand || 'Card').toUpperCase();
    if (payment.paymentMethodLast4) {
      return `${brand} •••• ${payment.paymentMethodLast4}`;
    }

    if (payment.paymentMethodId) {
      const method = paymentMethods.find((item) => String(item.id) === String(payment.paymentMethodId));
      if (method) {
        const methodBrand = (method.brand || 'Card').toUpperCase();
        if (method.last4) {
          return `${methodBrand} •••• ${method.last4}`;
        }

        return methodBrand;
      }
    }

    return brand === 'CARD' ? 'Credit Card' : brand;
  };

  return (
    <div className="col px-0">
      <div className="field-container px-2 mb-5">
        <div className="panel theme-gradient-2 py-1 px-2 flex aic mb-3">
          <h2 className="f-14 white bold">PAYMENTS</h2>
        </div>
        {sortedPayments.length === 0 ? (
          <div className="f-14 blueGray">No payments found.</div>
        ) : (
          <div className="table-container" role="table" aria-label="Payments">
            <div className="flex-table header panel mb-1 aic py-0" role="rowgroup">
              <div className="flex-row first" role="columnheader">
                DATE ISSUED
              </div>
              <div className="flex-row" role="columnheader">
                DETAILS
              </div>
              <div className="flex-row" role="columnheader">
                AMOUNT
              </div>
              <div className="flex-row" role="columnheader">
                STATUS
              </div>
            </div>
            {sortedPayments.map((payment) => {
              const statusBadge = getStatusBadge(payment.status);
              return (
                <div className="flex-table" role="rowgroup" key={payment.id}>
                  <div className="flex-row first" role="cell">
                    {formatUtcDateToUserTimeZone(payment.createdAt, userTimezone) ?? '—'}
                  </div>
                  <div className="flex-row" role="cell">
                    <div className="f-14 bold">Payment #{payment.id}</div>
                    <div className="f-1 blueGray">{getPaymentMethodLabel(payment)}</div>
                  </div>
                  <div className="flex-row" role="cell">
                    <div className="f-1 blueGray mb-1">Payment amount</div>
                    <div className="f-14 bold">{formatCentsAsCurrency(payment.amount, payment.currency)}</div>
                  </div>
                  <div className="flex-row" role="cell">
                    <span className={`d-inline-flex ${statusBadge.className}`}>{statusBadge.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
